import React, {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { config } from "../config/config";
import {
  HeaderHelpIconPropsType,
  HoverTooltipPropsType,
} from "./MonitoringTooltipTypes";

const { TOOLTIP_GAP, TOOLTIP_MARGIN } = config;

export function buildSimpleTooltip(title: string, value?: any) {
  return { kind: "simple", title, value: value ?? "unknown" };
}

export function buildDnsTooltip(title: string, dnsRecords?: any[] | null) {
  const list = Array.isArray(dnsRecords) ? dnsRecords : [];
  const resolved = list.filter((x) => x?.resolved);
  const unresolved = list.filter((x) => x && !x.resolved);
  return { kind: "dns", title, resolved, unresolved };
}

export function buildDomainsTooltip(title: string, domains?: string[] | null) {
  const list = Array.isArray(domains) ? domains : [];
  return { kind: "domains", title, domains: list };
}

export const HoverTooltip = forwardRef<HTMLDivElement, HoverTooltipPropsType>(
  function HoverTooltip({ data, scrollRef }, tooltipRootRef) {
    const localRef = useRef<HTMLDivElement>(null);
    const ref = (tooltipRootRef as React.RefObject<HTMLDivElement>) ?? localRef;
    const [pos, setPos] = useState({ left: 0, top: 0 });

    useLayoutEffect(() => {
      if (!data?.visible) return;
      const el = ref.current;
      const vw = window.innerWidth || 1;
      const vh = window.innerHeight || 1;
      /* istanbul ignore next */
      if (!el) return;
      let left = data.x + TOOLTIP_GAP;
      let top = data.y + TOOLTIP_GAP;
      const rect = el.getBoundingClientRect();
      if (left + rect.width + TOOLTIP_MARGIN > vw) {
        left = data.x - rect.width - TOOLTIP_GAP;
      }
      if (top + rect.height + TOOLTIP_MARGIN > vh) {
        top = data.y - rect.height - TOOLTIP_GAP;
      }
      left = Math.max(
        TOOLTIP_MARGIN,
        Math.min(left, vw - rect.width - TOOLTIP_MARGIN),
      );
      top = Math.max(
        TOOLTIP_MARGIN,
        Math.min(top, vh - rect.height - TOOLTIP_MARGIN),
      );
      setPos({ left, top });
    }, [
      ref,
      data?.visible,
      data?.x,
      data?.y,
      data?.kind,
      data?.title,
      data?.value,
      data?.resolved?.length,
      data?.unresolved?.length,
      data?.domains?.length,
    ]);

    if (!data?.visible) return null;

    return (
      <div
        ref={ref}
        className="hover-tooltip"
        data-testid="hover-tooltip"
        style={{ left: pos.left, top: pos.top }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="hover-tooltip-title">{data.title}</div>
        {data.kind === "domains" ? (
          <div className="hover-tooltip-scroll" ref={scrollRef}>
            <ul className="hover-tooltip-list">
              {data.domains.map((domain: string) => (
                <li key={domain} className="hover-tooltip-item">
                  {domain}
                </li>
              ))}
            </ul>
            {!data.domains.length && (
              <div className="hover-tooltip-empty">—</div>
            )}
          </div>
        ) : data.kind === "dns" ? (
          <div className="hover-tooltip-scroll" ref={scrollRef}>
            <div className="hover-tooltip-section">
              <div className="hover-tooltip-section-title">
                <span className="hover-tooltip-dot hover-tooltip-dot-ok" />
                resolved ({data.resolved.length})
              </div>
              {data.resolved.length ? (
                <ul className="hover-tooltip-list">
                  {data.resolved.map((p: any) => (
                    <li key={`ok-${p.dns_name}`} className="hover-tooltip-item">
                      {p.dns_name}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="hover-tooltip-empty">—</div>
              )}
            </div>
            <div className="hover-tooltip-section">
              <div className="hover-tooltip-section-title">
                <span className="hover-tooltip-dot hover-tooltip-dot-bad" />
                unresolved ({data.unresolved.length})
              </div>
              {data.unresolved.length ? (
                <ul className="hover-tooltip-list">
                  {data.unresolved.map((p: any) => (
                    <li
                      key={`bad-${p.dns_name}`}
                      className="hover-tooltip-item"
                    >
                      <span>{p.dns_name}</span>
                      {p.error ? (
                        <span className="hover-tooltip-error">({p.error})</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="hover-tooltip-empty">—</div>
              )}
            </div>
          </div>
        ) : (
          <div className="hover-tooltip-simple">
            <span className="hover-tooltip-dot hover-tooltip-dot-neutral" />
            {data.value}
          </div>
        )}
      </div>
    );
  },
);

export const HeaderHelpIcon: React.FC<HeaderHelpIconPropsType> = ({
  title,
  text,
  showTooltip,
  moveTooltip,
  hideTooltip,
  alwaysShow = false,
  className = "",
}) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const showTooltipRef = useRef(showTooltip);
  const titleRef = useRef(title);
  const textRef = useRef(text);

  useEffect(() => {
    if (!alwaysShow || !spanRef.current) return;
    const rect = spanRef.current.getBoundingClientRect();
    showTooltipRef.current(
      {
        currentTarget: spanRef.current,
        clientX: rect.right,
        clientY: rect.bottom,
      },
      buildSimpleTooltip(titleRef.current, textRef.current),
    );
  }, [alwaysShow]);

  return (
    <span
      ref={spanRef}
      className={`th-help${className ? ` ${className}` : ""}`}
      onMouseEnter={(e) => showTooltip(e, buildSimpleTooltip(title, text))}
      onMouseMove={moveTooltip}
      onMouseLeave={alwaysShow ? undefined : hideTooltip}
      role="img"
      aria-label={`Справка: ${title}`}
    >
      ?
    </span>
  );
};
